import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ValidationError } from 'yup';
import api from '~/services/api';
import { IFile } from '~/services/modules/contract/types';
import useStore, { storeState } from '~/store';
import { getErrorMessage, logErrorWithContext } from '~/utils/errorHandler';
import { reportError } from '~/utils/reportError';
import { ContractFlowHookResult, FilesError, FilesState, TypeFile } from './types';

interface RenewContractResponse {
  data: {
    contract_id: number;
    contract_code: string;
    insured: {
      name: string;
      mobile_phone: string;
      email: string;
      document: string;
      birthdate: string;
      address: {
        street: string;
        number: string;
        additional_details: string | null;
        zipcode: string;
        neighborhood: string;
        city: string;
        state: string;
        country: string;
      };
    };
    insured_item: {
      type_insured_item: string;
      manufacturer: string;
      model: string;
      serial_number: string;
      year_of_manufacture: number;
      insured_amount: number;
      total_value_accessories: number;
      risk_address: string | null;
      is_new: boolean;
      has_invoice: boolean;
      participates_competition: boolean;
      equipment_material_type: number;
      international_coverage: number;
      equipment_type_id: number;
      equipment_type: string;
    };
    plan: {
      name: string;
      has_club: boolean;
      club_name: string | null;
      club_description: string | null;
      products: Array<{
        product_id: number;
        name: string;
        price: number;
      }>;
      has_moderation: boolean;
      club_id: number | null;
      type_club: number | null;
      club_value: number | null;
      membership_fee: number;
      club_covers: any[];
    };
    total: number;
    financial_charges: number;
    flat_rate: number;
  };
}

export const useRenewFlow = (contractId: string | null): ContractFlowHookResult => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [errors, setErrors] = useState<FilesError>({} as FilesError);
  const [hasVoucherError, setHasVoucherError] = useState(false);
  const [contractData, setContractData] = useState<any>(null);
  const [submitError, setSubmitError] = useState<{
    title: string;
    message: string;
    type: 'error' | 'success' | 'warning';
  } | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<{
    title: string;
    message: string;
    type: 'error' | 'success' | 'warning';
  } | null>(null);
  const [files, setFiles] = useState<FilesState>({
    invoice: null,
    overview: null,
    serial_number_photo: null,
    ratchets_group: null,
    other: null
  });

  const {
    setContractId,
    setHasInvoice,
    setIsNewEquipment,
    setParticipatesInCompetitions,
    setEquipmentMaterial,
    setInternationalCoverage,
    setTotalValue,
    setServiceCharge,
    contractAsyncPostFiles
  } = useStore((store) => ({
    setContractId: store.setContractId,
    setHasInvoice: store.setHasInvoice,
    setIsNewEquipment: store.setIsNewEquipment,
    setParticipatesInCompetitions: store.setParticipatesInCompetitions,
    setEquipmentMaterial: store.setEquipmentMaterial,
    setInternationalCoverage: store.setInternationalCoverage,
    setTotalValue: store.setTotalValue,
    setServiceCharge: store.setServiceCharge,
    contractAsyncPostFiles: store.contractAsyncPostFiles
  }));

  useEffect(() => {
    async function fetchRenewContractData(cid: string) {
      setLoading(true);
      try {
        const response = await api.v2?.get<RenewContractResponse>(`Contracts/${cid}`);

        if (!response?.data?.data) {
          throw new Error('Dados do contrato não encontrados');
        }

        const contractData = response.data.data;
        setContractData(contractData);

        setContractId(contractData.contract_id);

        setHasInvoice(contractData.insured_item.has_invoice);
        setIsNewEquipment(contractData.insured_item.is_new);
        setParticipatesInCompetitions(contractData.insured_item.participates_competition);
        setEquipmentMaterial(contractData.insured_item.equipment_material_type);
        setInternationalCoverage(contractData.insured_item.international_coverage === 1);

        setTotalValue(contractData.total);
        setServiceCharge(contractData.financial_charges);

        storeState.insured = {
          ...contractData.insured,
          account_activated: false,
          address: {
            ...contractData.insured.address,
            additional_details: contractData.insured.address.additional_details || ''
          }
        };

        storeState.insured_item = {
          type_insured_item: contractData.insured_item.type_insured_item,
          manufacturer: contractData.insured_item.manufacturer,
          model: contractData.insured_item.model,
          serial_number: contractData.insured_item.serial_number,
          year_of_manufacture: contractData.insured_item.year_of_manufacture,
          insured_amount: contractData.insured_item.insured_amount,
          total_value_accessories: contractData.insured_item.total_value_accessories,
          is_new: contractData.insured_item.is_new,
          has_invoice: contractData.insured_item.has_invoice,
          participates_competition: contractData.insured_item.participates_competition,
          equipment_material_type: contractData.insured_item.equipment_material_type,
          international_coverage: contractData.insured_item.international_coverage,
          equipment_type: contractData.insured_item.equipment_type,
          equipment_type_id: contractData.insured_item.equipment_type_id
        };

        storeState.plan = contractData.plan;
        storeState.has_club = contractData.plan.has_club;
        storeState.club_name = contractData.plan.club_name || '';
        storeState.club_description = contractData.plan.club_description || '';
        storeState.flat_rate = contractData.flat_rate;

        setDataLoaded(true);
      } catch (error) {
        setHasVoucherError(true);

        const errorContext = logErrorWithContext(error, 'renew_flow_fetch_contract', {
          contractId: cid
        });

        reportError('Erro ao buscar dados do contrato de renovação', error, {
          context: 'renew_flow_fetch_contract',
          contractId: cid,
          errorType: errorContext.errorType
        });
      } finally {
        setLoading(false);
      }
    }

    if (contractId) {
      fetchRenewContractData(contractId);
    }
  }, [contractId]);

  const onChangeFile = (key: TypeFile, value: IFile) => {
    setFiles((state) => ({
      ...state,
      [key]: value
    }));
    setErrors((state) => ({ ...state, [key]: false }));
  };

  const onSubmit = async () => {
    if (!contractData?.contract_id) {
      setSubmitError({
        title: 'Erro',
        message: 'Dados do contrato não encontrados',
        type: 'error'
      });
      return;
    }

    try {
      setLoading(true);
      setSubmitError(null);
      setSubmitSuccess(null);

      const orgParam = searchParams.get('org');
      await contractAsyncPostFiles(contractData.contract_id, files, orgParam ?? undefined);

      setSubmitSuccess({
        title: 'Sucesso!',
        message: 'Fotos enviadas com sucesso!',
        type: 'success'
      });

      setTimeout(() => {
        navigate(`/contract/renewal/success/${contractId}${orgParam ? `?org=${orgParam}` : ''}`);
      }, 1500);
    } catch (error: any) {
      setLoading(false);
      if (error instanceof ValidationError) {
        const fieldsToCheck: TypeFile[] = ['invoice', 'overview', 'serial_number_photo', 'ratchets_group', 'other'];
        const fieldsError = error.errors;
        const newErrors = {} as FilesError;

        fieldsToCheck.forEach((field) => {
          newErrors[field] = fieldsError.includes(field);
        });

        setErrors(newErrors);
      } else {
        const errorContext = logErrorWithContext(error, 'renew_flow_upload_files', {
          contractId: contractData.contract_id,
          filesCount: Object.keys(files).length
        });

        const errorResponse = getErrorMessage(error);
        setSubmitError(errorResponse);

        reportError(`Erro ao processar arquivos no fluxo de renovação: ${errorResponse.title}`, error, {
          context: 'renew_flow_upload_files',
          contractId: contractData.contract_id,
          errorType: errorContext.errorType,
          filesCount: Object.keys(files).length
        });
      }
    }
  };

  return {
    loading,
    dataLoaded,
    hasVoucherError,
    files,
    errors,
    hasInvoice: contractData?.insured_item?.has_invoice || false,
    isNew: contractData?.insured_item?.is_new || false,
    product: contractData?.insured_item?.equipment_type_id,
    productId: contractData?.plan?.products?.map((p: any) => p.product_id) || [],
    voucher: '',
    isEmailMode: true,
    documentUrls: {},
    submitError,
    submitSuccess,
    onChangeFile,
    onSubmit
  };
};
